import React, { useState } from "react";
import { Mail, Zap, Sparkles, Users, CheckCircle2 } from "lucide-react";

export default function WaitlistPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const GOOGLE_SCRIPT_URL =
    "https://script.google.com/macros/s/AKfycbywrlxr7ymdzQB10sciutb3ixvGLi8A3RAJXIWuAvheDK_ocbe3dP1eGX51WTyqSGj-AA/exec";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setLoading(true);

    try {
      const res = await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        body: JSON.stringify({ email }),
        headers: { "Content-Type": "text/plain" },
      });

      const data = await res.json();

      if (data.status === "success") {
        setStatus({ type: "success", message: "You're on the list! 🚀" });
        setEmail("");
      } else {
        throw new Error("Failed");
      }
    } catch (err) {
      setStatus({ type: "error", message: "Something went wrong. Try again!" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-purple-950 to-slate-950 text-white">
      {/* Nav */}
      <nav className="container mx-auto px-6 py-8 flex justify-between items-center">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
          FutureApp1
        </h1>

        <button className="btn btn-outline border-purple-500 text-purple-300 hover:bg-purple-500/20">
          Sign In
        </button>
      </nav>

      {/* Hero + Waitlist */}
      <section className="container mx-auto px-6 pt-20 pb-32 text-center">
        <div className="badge badge-lg bg-purple-900/50 border border-purple-700 mb-6 gap-2 py-3 px-4">
          <Sparkles className="w-4 h-4" />
          Launching Q1 2026
        </div>

        <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
          The Future Is
          <br />
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-red-400 bg-clip-text text-transparent">
            Coming Fast
          </span>
        </h1>

        <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-12">
          Join 18,000+ early adopters getting exclusive access and shaping what’s next.
        </p>

        {/* DaisyUI Card */}
        <div className="card max-w-md mx-auto bg-white/5 backdrop-blur-md border border-purple-500/30 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2 text-left">
              <label htmlFor="email" className="text-lg font-medium">
                Your email
              </label>

              <input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input input-bordered w-full bg-white/10 border-purple-500/50 text-white placeholder-gray-400 focus:border-purple-400"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-lg w-full bg-gradient-to-r from-purple-600 to-pink-600 border-none hover:from-purple-700 hover:to-pink-700 text-white"
            >
              {loading ? "Joining..." : "Join the Waitlist"}
              <Mail className="ml-2 w-5 h-5" />
            </button>

            {status && (
              <div
                className={`flex items-center gap-3 p-4 rounded-lg border ${
                  status.type === "success"
                    ? "bg-green-500/10 border-green-500/50 text-green-400"
                    : "bg-red-500/10 border-red-500/50 text-red-400"
                }`}
              >
                <CheckCircle2 className="w-5 h-5" />
                <span>{status.message}</span>
              </div>
            )}

            <div className="flex items-center justify-center gap-2 text-gray-400 mt-2">
              <Users className="w-5 h-5" />
              <span className="font-medium">18,492</span> people already joined
            </div>
          </form>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-6 py-24">
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { icon: Zap, title: "Lightning Fast", desc: "Built for speed from day one" },
            { icon: Sparkles, title: "AI-Native", desc: "Intelligence baked into everything" },
            { icon: Users, title: "Community First", desc: "You help build the product" },
          ].map((f, i) => (
            <div
              key={i}
              className="card bg-white/5 border border-purple-500/20 p-8 text-center hover:bg-white/10 transition"
            >
              <f.icon className="w-12 h-12 mx-auto mb-4 text-purple-400" />
              <h3 className="text-2xl font-bold mb-3">{f.title}</h3>
              <p className="text-gray-400">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 text-center text-gray-500">
        <p>© 2025 FutureApp. All rights reserved.</p>
      </footer>
    </div>
  );
}
